import moment from 'moment';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Changelog } from './types';

interface ChangelogEntryProps {
    changelog: Changelog;
    unread?: boolean;
}

const heading = ({ children }: { children?: React.ReactNode }) => (
    <h3 className="tw-m-0 tw-mb-1.5 tw-mt-5 tw-border-0 tw-border-t tw-border-solid tw-border-accents-2 tw-pt-5 tw-text-base tw-font-semibold tw-leading-snug tw-text-accents-9 first:tw-mt-0 first:tw-border-t-0 first:tw-pt-0">
        {children}
    </h3>
);

const markdownComponents: Components = {
    h1: heading,
    h2: heading,
    h3: heading,
    h4: heading,
    p: ({ children }) => (
        <p className="tw-m-0 tw-mb-2 tw-text-sm tw-leading-relaxed tw-text-accents-7 last:tw-mb-0">{children}</p>
    ),
    strong: ({ children }) => <strong className="tw-font-semibold tw-text-accents-9">{children}</strong>,
    ul: ({ children }) => (
        <ul className="tw-m-0 tw-mb-2 tw-flex tw-list-disc tw-flex-col tw-gap-1 tw-pl-5 tw-text-sm tw-leading-relaxed tw-text-accents-7">
            {children}
        </ul>
    ),
    ol: ({ children }) => (
        <ol className="tw-m-0 tw-mb-2 tw-flex tw-list-decimal tw-flex-col tw-gap-1 tw-pl-5 tw-text-sm tw-leading-relaxed tw-text-accents-7">
            {children}
        </ol>
    ),
    a: ({ href, children }) => (
        <Link href={href ?? '#'} className="tw-font-semibold tw-text-primary tw-underline">
            {children}
        </Link>
    )
};

const ChangelogEntry = ({ changelog, unread }: ChangelogEntryProps) => {
    const { t } = useTranslation('common');

    return (
        <article className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-accents-5">
                {unread && <span className="tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full tw-bg-primary" />}
                {t('changelog.entryTitle', { date: moment(changelog.published_at).format('DD.MM.YYYY') })}
            </div>
            <div>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {changelog.body}
                </ReactMarkdown>
            </div>
        </article>
    );
};

export default ChangelogEntry;
